<template>
  <div class="section-container pb-2">
    <div
      class="section-title d-flex align-center px-6 py-3 subtitle-1 font-weight-medium"
    >
      <v-icon color="primary" class="mr-2">mdi-clock-outline</v-icon>
      <span>{{ $t("shifts") }}</span>
    </div>
    <v-divider></v-divider>

    <div class="section-content-shifts">
      <!-- Toggle za custom nazive smena -->
      <div class="shifts-grid" :style="{gap: shiftsGrid + 'px'}">
        <v-checkbox
          v-model="model.is_translate_shift"
          @change="toggleCustomNames"
          :true-value="1"
          :false-value="0"
          color="primary"
          dense
          :label="$t('change_shift_names')"
          hide-details
        />
      </div>
      <!-- Lista smena -->
      <div class="shifts-grid" :style="{gap: shiftsGrid + 'px'}">
        <div v-for="shift in shifts" :key="shift.key" class="shift-item">
          <div class="shift-header">
            <v-checkbox
              v-model="model[shift.valueKey]"
              :true-value="shift.value"
              :false-value="0"
              @change="onShiftToggle(shift, $event)"
              color="primary"
              dense
              hide-details
              class="shift-checkbox mt-0 pt-2"
            />
            <v-icon color="primary" size="16">{{ shift.icon }}</v-icon>
            <span class="shift-name">{{ $t(shift.name) }}</span>
          </div>


          <!-- Custom nazivi za svaki jezik -->
          <div
            v-if="model[shift.valueKey] === shift.value && model.is_translate_shift === 1"
            class="shift-translations"
          >
          {{ model.shiftTranslations }}
            <div
              v-for="language in availableLanguages"
              :key="language.locale"
              class="language-translation"
            >
              <small class="language-label">{{ language.text }}</small>
              <text-field
                :value="getTranslationLabel(language.locale, shift.activeKey)"
                @input="updateTranslationLabel(language.locale, shift.activeKey, $event)"
                :placeholder="getDefaultShiftName(language.locale, shift.activeKey)"
                hide-details
                class="shift-input"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Legend Section -->
      <div class="legend-section" v-if="activeShifts.length">
        <div class="legend-header">
          <v-icon color="primary" size="18" class="mr-2">mdi-information</v-icon>
          <span>{{ $t("shifts") }}</span>
        </div>
        <div class="legend-items">
          <div v-for="shift in activeShifts" :key="shift.key" class="legend-item">
            <v-icon :color="shift.color" size="16">{{ shift.icon }}</v-icon>
            <span class="legend-text">{{ $t(shift["displayName"]) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import hasModel from "@/mixins/hasModel.js";

export default {
  name: "ShiftManagement",
  mixins: [hasModel],
  props: {
    shiftsGrid: {
      type: Number,
      default: 16,
    },
  },

  data() {
    return {
      isCustomNamesEnabled: false,
    };
  },

  mounted() {
    this.initializeTranslations();
  },

  computed: {
    shiftStates() {
      return {
        shift_1: this.hasTranslationsForShift("shift_1"),
        shift_2: this.hasTranslationsForShift("shift_2"),
        shift_3: this.hasTranslationsForShift("shift_3"),
        shift_4: this.hasTranslationsForShift("shift_4"),
      };
    },

    hasActiveShifts() {
      return this.shifts.some((shift) => this.hasTranslationsForShift(shift.activeKey));
    },

    activeShifts() {
      return this.shifts
        .filter((shift) => this.model[shift.valueKey] === shift.value)
        .map((shift) => ({
          ...shift,
          displayName: shift.name,
        }));
    },

    availableLanguages() {
      const restaurantLanguages = this.$store.state.restaurant?.settings?.language || [];
      return this.$i18n.availableLocales
        .filter((locale) => restaurantLanguages.includes(locale))
        .map((locale) => ({
          locale,
          text: this.getLanguageText(locale),
        }));
    },

    shifts() {
      return [
        {
          key: "shift1",
          activeKey: "shift_1",
          valueKey: "shift_one",
          nameKey: "shift_1_name",
          name: "first_shift",
          displayName: "first_shift",
          value: 1,
          icon: "mdi-weather-sunny",
          color: "#FFC107",
        },
        {
          key: "shift2",
          activeKey: "shift_2",
          valueKey: "shift_two",
          nameKey: "shift_2_name",
          name: "second_shift",
          displayName: "second_shift",
          value: 2,
          icon: "mdi-weather-sunset",
          color: "#FF9800",
        },
        {
          key: "shift3",
          activeKey: "shift_3",
          valueKey: "shift_three",
          nameKey: "shift_3_name",
          name: "third_shift",
          displayName: "third_shift",
          value: 3,
          icon: "mdi-weather-night",
          color: "#3F51B5",
        },
        {
          key: "shift4",
          activeKey: "shift_4",
          valueKey: "shift_four",
          nameKey: "shift_4_name",
          name: "fourth_shift",
          displayName: "fourth_shift",
          value: 4,
          icon: "mdi-clock",
          color: "#3F51B5",
        },
      ];
    },

    shiftLanguage() {
      return {
        sr: {
          shift_1: "Доручак",
          shift_2: "Ручак",
          shift_3: "Вечера",
          shift_4: "Ноћна смена",
        },
        en: {
          shift_1: "Breakfast",
          shift_2: "Lunch",
          shift_3: "Dinner",
          shift_4: "Night Shift",
        },
        rs: {
          shift_1: "Доручак",
          shift_2: "Ручак",
          shift_3: "Вечера",
          shift_4: "Ноћна смена",
        },
        hr: {
          shift_1: "Doručak",
          shift_2: "Ručak",
          shift_3: "Večera",
          shift_4: "Noćna smena",
        },
        bs: {
          shift_1: "Doručak",
          shift_2: "Ručak",
          shift_3: "Večera",
          shift_4: "Noćna smjena",
        },
        ru: {
          shift_1: "Завтрак",
          shift_2: "Обед",
          shift_3: "Ужин",
          shift_4: "Ночная смена",
        },
        ba: {
          shift_1: "Doručak",
          shift_2: "Ručak",
          shift_3: "Večera",
          shift_4: "Noćna smena",
        },
        mk: {
          shift_1: "Појадок",
          shift_2: "Ручек",
          shift_3: "Вечера",
          shift_4: "Нощна смена",
        },
        "sr-lat": {
          shift_1: "Doručak",
          shift_2: "Ručak",
          shift_3: "Večera",
          shift_4: "Noćna smena",
        },
        "rs-lat": {
          shift_1: "Doručak",
          shift_2: "Ručak",
          shift_3: "Večera",
          shift_4: "Noćna smena",
        },
        si: {
          shift_1: "Zajtrk",
          shift_2: "Kosilo",
          shift_3: "Večerja",
          shift_4: "Nočna izmena",
        },
      };
    },
  },

  methods: {
    // === JEDNOSTAVAN PRISTUP ZA UPRAVLJANJE PREVODIMA ===

    // Inicijalizacija
    initializeTranslations() {
      if (!this.model.shiftTransation) {
        this.$set(this.model, "shiftTransation", []);
      }
      this.isCustomNamesEnabled = this.model.is_translate_shift === 1;
    },

    // Pronađi prevod
    findTranslation(languageCode, shiftCode) {
      if (!this.model.shiftTransation) return null;
      return this.model.shiftTransation.find(
        (t) => t.language_code === languageCode && t.shift_code === shiftCode
      );
    },

    // Kreiraj novi prevod
    createTranslation(languageCode, shiftCode, label = "") {
      if (!this.model.shiftTransation) {
        this.$set(this.model, "shiftTransation", []);
      }

      const translation = {
        company_id: this.model.id,
        restaurant_id: this.$store.state.restaurant?.id,
        shift_code: shiftCode,
        language_code: languageCode,
        label: label || this.getDefaultShiftName(languageCode, shiftCode),
      };

      this.model.shiftTransation.push(translation);
      return translation;
    },

    // Dobij label za prevod (koristi se u template-u)
    getTranslationLabel(languageCode, shiftCode) {
      const translation = this.findTranslation(languageCode, shiftCode);
      return translation ? translation.label : "";
    },

    // Ažuriraj label prevoda (koristi se u template-u)
    updateTranslationLabel(languageCode, shiftCode, newLabel) {
      let translation = this.findTranslation(languageCode, shiftCode);

      if (!translation) {
        translation = this.createTranslation(languageCode, shiftCode, newLabel);
      } else {
        translation.label = newLabel;
      }
    },

    // Toggle custom naziva smena
    toggleCustomNames() {
      this.isCustomNamesEnabled = this.model.is_translate_shift === 1;

      if (this.isCustomNamesEnabled) {
        // Kreiraj prevode za sve aktivne smene
        this.createTranslationsForActiveShifts();
      } else {
        // Obriši sve prevode
        this.model.shiftTransation = [];
      }
    },

    // Provjeri da li postoje prevodi za određenu smenu
    hasTranslationsForShift(shiftCode) {
      if (!this.model.shiftTransation) return false;
      return this.model.shiftTransation.some((t) => t.shift_code === shiftCode);
    },

    // Handle aktivacija/deaktivacija smene
    onShiftToggle(shift, isChecked) {
      const shiftCode = shift.activeKey;

      if (isChecked) {
        // Smena aktivirana - kreiraj prevode za sve jezike
        this.availableLanguages.forEach((lang) => {
          if (!this.findTranslation(lang.locale, shiftCode)) {
            this.createTranslation(lang.locale, shiftCode);
          }
        });
      } else {
        // Smena deaktivirana - obriši prevode
        this.removeTranslationsForShift(shiftCode);
      }
    },

    // Kreiraj prevode za sve aktivne smene
    createTranslationsForActiveShifts() {
      this.shifts.forEach((shift) => {
        if (this.hasTranslationsForShift(shift.activeKey)) {
          this.availableLanguages.forEach((lang) => {
            if (!this.findTranslation(lang.locale, shift.activeKey)) {
              this.createTranslation(lang.locale, shift.activeKey);
            }
          });
        }
      });
    },

    // Ukloni prevode za određenu smenu
    removeTranslationsForShift(shiftCode) {
      if (!this.model.shiftTransation) return;

      this.model.shiftTransation = this.model.shiftTransation.filter(
        (t) => t.shift_code !== shiftCode
      );
    },

    // Pomoćne metode
    getDefaultShiftName(languageCode, shiftCode) {
      return (
        this.shiftLanguage[languageCode]?.[shiftCode] ||
        this.shiftLanguage.en[shiftCode]
      );
    },

    getLanguageText(locale) {
      const languageNames = {
        bs: "Bosanski",
        en: "English",
        hr: "Hrvatski",
        mk: "Македонски",
        "rs-lat": "Srpski - latinica",
        rs: "Српски - ћирилица",
        "sr-lat": "Srpski - latinica",
        sr: "Српски - ћирилица",
        si: "Slovenščina",
      };
      return languageNames[locale] || locale;
    },
  },
};
</script>

<style scoped>
/* Section specific styles for shift management */
.section-content-shifts {
  padding: 24px;
}

.shifts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  margin-bottom: 24px;
}

.shift-item {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 16px;
  transition: all 0.2s ease;
}

.shift-item:hover {
  background: #e9ecef;
  border-color: #ff6b35;
}

.shift-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.shift-name {
  font-size: 14px;
  font-weight: 600;
  color: #495057;
  margin-left: 8px;
}

.shift-translations {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  background: white;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.language-translation {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.language-label {
  font-size: 12px;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.shift-input ::v-deep .v-input__slot {
  border-color: #e9ecef !important;
}

.shift-input ::v-deep .v-input--is-focused .v-input__slot {
  border-color: #ff6b35 !important;
  box-shadow: 0 0 0 2px rgba(255, 107, 53, 0.1) !important;
}

/* Legend section */
.legend-section {
  background: rgba(255, 107, 53, 0.08);
  border: 1px solid rgba(255, 107, 53, 0.2);
  border-radius: 8px;
  padding: 16px;
}

.legend-header {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  color: #ff6b35;
  margin-bottom: 12px;
}

.legend-items {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: white;
  border-radius: 16px;
  border: 1px solid #e9ecef;
}

.legend-text {
  font-size: 12px;
  font-weight: 500;
  color: #495057;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .shifts-grid {
    grid-template-columns: 1fr;
  }

  .legend-items {
    flex-direction: column;
    gap: 8px;
  }
}
</style>
